import { entities } from '@shared/constants/entities';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import {
  Dialog,
  DialogContent,
  DialogSub,
  DialogSubContent,
  DialogSubTrigger,
  DialogTrigger,
} from '@src/design-system/dialog';
import { dayjsUtils } from '@shared/utils/helpers/dayjs';
import { HasPermission } from '@features/roles/guards/has-permission';
import type { TMeetingNote } from '@shared/types/meeting-note';
import { Edit2, Trash } from 'iconsax-react';
import { CreateNoteSubModal } from './sub-modals/create-note-sub-modal';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';
import meetingNoteApi from '../../../../../services/meeting-note-api';
import { useGetMe } from '@shared/services/get-me';

export const AppointmentNoteCard = ({
  created_by,
  created_by_partner,
  created_at,
  note,
  id,
  meeting,
}: TMeetingNote) => {
  const { mutateAsync, isPending } = meetingNoteApi.useDelete();
  const formattedDate = dayjsUtils(created_at).format('D MMM YYYY');
  const { data: currentMember } = useGetMe();
  const isPartner = currentMember?.type === 'PARTNER';
  const isCreatedByCurrentUser =
    !!currentMember &&
    (isPartner && created_by_partner
      ? created_by_partner.id === currentMember.user.id
      : created_by?.id === currentMember.user.id);

  const handleDelete = async () => {
    await mutateAsync({ id });
  };

  return (
    <div className='flex flex-col p-4 bg-white rounded-xl border border-primary-200 min-w-64 flex-shrink-0'>
      <div className='flex items-center gap-3 mb-3'>
        <Avatar className='size-10'>
          <AvatarImage src={created_by.avatar} />
          <AvatarFallback colorId={created_by.id}>
            {created_by.first_name.substring(0, 1)}
          </AvatarFallback>
        </Avatar>
        <div>
          <span className='font-medium text-sm text-gray-800'>
            {created_by.first_name + ' ' + created_by.last_name}
          </span>
          <div className='flex items-center text-gray-600 text-sm'>
            <span>{formattedDate}</span>
          </div>
        </div>
      </div>
      <div dangerouslySetInnerHTML={{ __html: note }} />
      {isCreatedByCurrentUser && (
        <div className='flex items-center gap-2 justify-end mt-2'>
          <HasPermission
            entity={entities.meetingNote}
            action='add'
          >
            <DialogSub>
              <DialogSubTrigger>
                <Edit2
                  size={20}
                  className='text-gray-800 hover:text-gray-600 cursor-pointer'
                />
              </DialogSubTrigger>
              <DialogSubContent>
                <CreateNoteSubModal
                  meetingId={meeting.id}
                  note={{ id, note }}
                />
              </DialogSubContent>
            </DialogSub>
          </HasPermission>
          <Dialog>
            <DialogTrigger>
              <Trash
                size={20}
                className='text-red-500 hover:text-red-600 cursor-pointer'
              />
            </DialogTrigger>
            <DialogContent align='center'>
              <DeleteConfirmDialog
                title='Are you sure you want to delete this note?'
                isLoading={isPending}
                onConfirm={handleDelete}
              />
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
};
